import React from 'react';
import { ArrowLeft, Check } from 'lucide-react';

const STEPS = ['Pickup', 'Drop-off', 'What', 'When', 'Details'];

/**
 * Post-a-delivery header: back action, title and a 5-step progress bar that
 * fills in as the customer completes each group of the form.
 */
export const PostDeliveryHeader = ({ onBack, done = [], current = 1, onStepClick }) => (
  <div className="mb-4" data-testid="post-delivery-header">
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        className="w-10 h-10 rounded-full border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-700 transition-colors active:scale-95 flex-shrink-0"
        data-testid="post-delivery-back-btn"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#050505]" data-testid="post-job-title">Post a Delivery</h1>
    </div>

    <ol className="mt-4 flex items-start" data-testid="post-delivery-stepper">
      {STEPS.map((label, i) => {
        const stepNo = i + 1;
        const isCurrent = stepNo === current;
        const isDone = stepNo < current && !!done[i];
        const isPast = stepNo < current;
        return (
          <li key={label} className="flex-1 flex flex-col items-center relative">
            {i > 0 && (
              <span
                className={`absolute top-4 right-1/2 left-0 h-0.5 -translate-y-1/2 ${stepNo <= current ? 'bg-[#2E7D32]' : 'bg-gray-200'}`}
                aria-hidden="true"
              />
            )}
            <button
              type="button"
              onClick={() => isPast && onStepClick && onStepClick(stepNo)}
              disabled={!isPast}
              className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors duration-300
                ${isDone
                  ? 'bg-[#2E7D32] border-[#2E7D32] text-white'
                  : isCurrent
                    ? 'bg-white border-[#2E7D32] text-[#1B5E20] ring-4 ring-emerald-100'
                    : isPast
                      ? 'bg-white border-[#2E7D32] text-[#1B5E20]'
                      : 'bg-white border-gray-200 text-gray-400'}`}
              data-testid={`post-step-${stepNo}`}
            >
              {isDone ? <Check className="w-4 h-4" strokeWidth={3} /> : stepNo}
            </button>
            <span className={`mt-1.5 text-[11px] font-semibold text-center leading-tight ${isDone || isCurrent || isPast ? 'text-[#1B5E20]' : 'text-gray-400'}`}>
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  </div>
);

/** White section card with a coloured icon tile and a question headline. */
export const FormSection = ({ icon: Icon, tone = 'green', title, hint, testid, children }) => {
  const tones = {
    green: 'bg-emerald-50 text-[#2E7D32] border-emerald-100',
    orange: 'bg-orange-50 text-[#E65100] border-orange-100',
    blue: 'bg-blue-50 text-[#1565C0] border-blue-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
  };
  return (
    <section className="rounded-2xl border border-gray-200/80 bg-white p-4 sm:p-5 mb-4" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <span className={`flex-shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </span>
        <div className="min-w-0 pt-0.5">
          <h2 className="text-base md:text-lg font-bold tracking-tight text-[#050505] leading-snug">{title}</h2>
          {hint && <p className="text-xs sm:text-sm text-gray-500 mt-0.5 leading-snug">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
};

/** Tile picker for the item type. */
export const ItemTypeTiles = ({ types, value, onChange }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="item-type-tiles">
    {types.map((t) => {
      const selected = value === t.value;
      return (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          aria-pressed={selected}
          className={`flex flex-col items-center justify-center gap-1.5 py-4 px-2 rounded-xl border-2 transition-all duration-200 active:scale-[0.97]
            ${selected
              ? 'border-[#2E7D32] bg-emerald-50 shadow-sm shadow-emerald-900/5'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}
          data-testid={`item-type-tile-${t.value}`}
        >
          <span aria-hidden="true" className={`text-2xl leading-none transition-transform ${selected ? 'scale-110' : ''}`}>
            {t.emoji || '📦'}
          </span>
          <span className={`text-xs font-bold text-center leading-tight ${selected ? 'text-[#1B5E20]' : 'text-[#374151]'}`}>
            {t.label}
          </span>
        </button>
      );
    })}
  </div>
);
